import React from "react";
import { Flex } from "antd";
import { Empty } from "antd";
import { Spinner } from "reactstrap";
import { AntSelect } from "@/CommonElements/AntSelect";
import { useDebouncedSearch } from "@/utils/hooks/useDebouncedSearch";

/**
 * Shared select with infinite scroll and server-side search.
 * Use with useInfiniteQuery-based hooks (e.g. useInfinitePrivateTransportContractsQuery, useInfiniteFlightDetailsAirlinesQuery).
 *
 * @param {Object} props
 * @param {Function} props.queryHook - The infinite query hook to use (e.g. useInfinitePrivateTransportContractsQuery)
 * @param {Object} props.queryParams - Additional params for the query hook (e.g. { pageSize: 10 })
 * @param {Function} props.getItemsFromPage - (page) => array of items from each page, e.g. (page) => page?.data?.data ?? []
 * @param {Function} props.mapItemToOption - (item) => ({ value, label }) for each item
 * @param {Array} [props.prependedItems] - Items to prepend once at the top (e.g. current selection in edit mode)
 * @param {number} props.debounceMs - Debounce delay for search in milliseconds (default: 500)
 * @param {Object} props.selectProps - Rest props passed to AntSelect (placeholder, status, etc.)
 */
export const InfiniteSearchSelect = ({
  queryHook,
  queryParams = {},
  getItemsFromPage,
  mapItemToOption,
  prependedItems,
  debounceMs = 500,
  ...selectProps
}) => {
  const { debouncedValue, setSearchValue } = useDebouncedSearch("", debounceMs);
  
  const queryResult = queryHook({ 
    search: debouncedValue,
    ...queryParams 
  });

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = queryResult;

  const pageItems = data?.pages?.flatMap(getItemsFromPage) ?? [];
  const prepended = Array.isArray(prependedItems) ? prependedItems : [];
  const allItems = [...prepended, ...pageItems];
  
  // Deduplicate by id so the prepended item doesn't appear twice
  const seenIds = new Set();
  const items = allItems.filter((item) => {
    const id = item?.id ?? item?.value;
    if (id == null || seenIds.has(id)) return false;
    seenIds.add(id);
    return true;
  });

  const handlePopupScroll = (e) => {
    const { target } = e;
    if (
      target.scrollTop + target.offsetHeight === target.scrollHeight &&
      hasNextPage &&
      !isFetchingNextPage
    ) {
      fetchNextPage();
    }
  };

  const options = items.map(mapItemToOption);

  return (
    <AntSelect
      size="large"
      showSearch
      onSearch={setSearchValue}
      onPopupScroll={handlePopupScroll}
      filterOption={false}
      notFoundContent={
        isLoading || isFetchingNextPage ? (
          <Flex align="center" justify="center" style={{ padding: "20px" }}>
            <Spinner size="sm" />
          </Flex>
        ) : (
          <Empty />
        )
      }
      dropdownRender={(menu) => (
        <>
          {menu}
          {isFetchingNextPage && (
            <div style={{ textAlign: "center", padding: "8px" }}>
              <Spinner size="sm" />
            </div>
          )}
        </>
      )}
      {...selectProps}
      options={options}
    />
  );
};
